import { useCallback, useEffect, useState } from "react";

/**
 * Local-only acknowledgement state for incidents.
 *
 * The backend does not yet have a server-side ack endpoint (see
 * web/README.md "Known gaps"). Until it does, this hook stores ack state
 * in localStorage so the UI is not blocking on backend work, BUT this
 * means two dispatchers will not see each other's acks. The operator name
 * shown in the card is the local viewer's own name.
 *
 * Migration plan when the backend lands:
 *   1. Replace this hook with one that calls POST /alerts/{id}/ack.
 *   2. Keep the same { ackedBy, ackedAt } shape on the returned record.
 *   3. Components consuming this hook do not change.
 */

const STORAGE_KEY = "occ.incident.ack.v2";

export interface AckRecord {
  ackedBy: string;
  ackedAt: number;
}

export interface AckMap {
  [incidentId: string]: AckRecord;
}

function load(): AckMap {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}") as AckMap;
  } catch {
    return {};
  }
}

function save(m: AckMap) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(m));
}

export interface UseIncidentAcksResult {
  acks: AckMap;
  ack: (incidentId: string) => void;
  unack: (incidentId: string) => void;
}

export function useIncidentAcks(operatorName: string): UseIncidentAcksResult {
  const [acks, setAcks] = useState<AckMap>(load);

  // Sync across tabs of the same operator.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return;
      setAcks(load());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const ack = useCallback((id: string) => {
    setAcks((prev) => {
      const next = { ...prev, [id]: { ackedBy: operatorName, ackedAt: Date.now() } };
      save(next);
      return next;
    });
  }, [operatorName]);

  const unack = useCallback((id: string) => {
    setAcks((prev) => {
      const next = { ...prev };
      delete next[id];
      save(next);
      return next;
    });
  }, []);

  return { acks, ack, unack };
}
